import { useState, useCallback, useMemo } from 'react';
import { FaRegFileExcel } from 'react-icons/fa';
import dayjs, { Dayjs } from 'dayjs';
import { Button, EmptyPlaceholder, exportToExcel, DatepickerV2, Pagination } from '@client/toolkit';
import { useGetInvoicesReceivableDetailsQuery, InvoiceStatus } from '@client/api';
import { InvoiceOverviewTable, SortDirection } from '../../pods/agreements/agreement-list/components/agreement-reports/invoice-overview-table.component';
import { InvoiceOverviewItem } from '../../pods/agreements/agreement-list/components/agreement-reports/mock-data';

interface InvoiceOverviewProps {
  companyId?: string;
  autoFetch?: boolean;
}

const PAGE_SIZE = 25;

export function InvoiceOverview({ companyId, autoFetch = false }: InvoiceOverviewProps) {
  const [hasGenerated, setHasGenerated] = useState(autoFetch);
  const [page, setPage] = useState(0);
  const [sortColumn, setSortColumn] = useState<string | undefined>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>('asc');

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  // Validate that dates are in the same year (required by receivable-details API)
  const dateYearMismatch =
    dateRange[0] && dateRange[1] && dateRange[0].year() !== dateRange[1].year();

  const {
    data: receivableResponse,
    isLoading: isLoadingReceivables,
    isFetching: isFetchingReceivables,
    error: receivablesError,
  } = useGetInvoicesReceivableDetailsQuery(
    {
      startDate: dateRange[0]?.format('YYYY-MM-DD'),
      endDate: dateRange[1]?.format('YYYY-MM-DD'),
      companyId,
      page: 1,
      pageSize: 10000,
    },
    { skip: !hasGenerated },
  );

  // Flatten nested receivable response into flat invoice items, deduplicated by invoiceNumber
  const invoiceData: InvoiceOverviewItem[] = useMemo(() => {
    if (!receivableResponse?.items) return [];
    const seen = new Set<number>();
    const items: InvoiceOverviewItem[] = [];
    for (const building of receivableResponse.items) {
      for (const tenant of building.tenantReceivables) {
        for (const invoice of tenant.invoices) {
          if (seen.has(invoice.invoiceNumber)) continue;
          seen.add(invoice.invoiceNumber);
          items.push({
            id: invoice.invoiceId,
            tenantName: tenant.tenantName,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: invoice.invoiceDate,
            dueDate: invoice.dueDate,
            amount: invoice.amount.value,
            amountOutstanding: invoice.amountOutstanding.value,
            currency: invoice.amountOutstanding.currency,
            status: invoice.status as InvoiceStatus,
          });
        }
      }
    }
    return items;
  }, [receivableResponse]);

  // Sort invoice data based on current sort state
  const sortedInvoiceData = useMemo(() => {
    if (!sortColumn || !sortDirection) return invoiceData;

    return [...invoiceData].sort((a, b) => {
      let aValue: any = a[sortColumn as keyof InvoiceOverviewItem];
      let bValue: any = b[sortColumn as keyof InvoiceOverviewItem];

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle number comparison
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [invoiceData, sortColumn, sortDirection]);

  const handleFromDateChange = useCallback((date: Dayjs | null) => {
    setDateRange((prev) => [date, prev[1]]);
    setHasGenerated(false);
  }, []);

  const handleToDateChange = useCallback((date: Dayjs | null) => {
    setDateRange((prev) => [prev[0], date]);
    setHasGenerated(false);
  }, []);

  const handleSort = useCallback((column: string, direction: SortDirection) => {
    setSortColumn(column);
    setSortDirection(direction);
    setPage(0);
  }, []);

  const handleGenerate = useCallback(() => {
    setHasGenerated(true);
    setPage(0);
  }, []);

  const handleExportExcel = useCallback(() => {
    if (sortedInvoiceData.length === 0) return;
    const statusLabels: Record<string, string> = {
      Paid: 'Betalt',
      PartiallyPaid: 'Delvis betalt',
      Unpaid: 'Ubetalt',
      Overdue: 'Forfalt',
      Credited: 'Kreditert',
      CreditNote: 'Kreditnota',
    };
    const exportData = sortedInvoiceData.map((item) => ({
      'Leietaker': item.tenantName,
      'Fakturanr': item.invoiceNumber,
      'Fakturadato': item.invoiceDate,
      'Forfallsdato': item.dueDate,
      'Beløp': item.amount === 0 && item.amountOutstanding !== 0 ? 'N/A' : item.amount,
      'Utestående': item.amountOutstanding,
      'Status': statusLabels[item.status] ?? item.status,
    }));
    exportToExcel(exportData, 'fakturaoversikt.xlsx');
  }, [sortedInvoiceData]);

  const isLoading = (isLoadingReceivables || isFetchingReceivables) && hasGenerated;

  const error = dateYearMismatch
    ? 'Fra- og til-dato må være i samme år.'
    : receivablesError
    ? 'Kunne ikke hente fakturaer. Prøv igjen senere.'
    : null;

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="w-12 h-12 border-2 border-brand-primary rounded-full animate-spin border-r-transparent" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="px-4 py-8">
          <EmptyPlaceholder>{error}</EmptyPlaceholder>
        </div>
      );
    }

    if (!hasGenerated) {
      return null;
    }

    if (sortedInvoiceData.length === 0) {
      return (
        <div className="px-4 py-8">
          <EmptyPlaceholder>Ingen resultater</EmptyPlaceholder>
        </div>
      );
    }

    const pagedData = sortedInvoiceData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    return (
      <>
        <InvoiceOverviewTable
          data={pagedData}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        <Pagination
          totalCount={sortedInvoiceData.length}
          currentPage={page}
          setPage={setPage}
          pageSize={PAGE_SIZE}
        />
      </>
    );
  };

  const hasData = sortedInvoiceData.length > 0;

  return (
    <>
      <div className="flex items-center gap-4 px-4 py-4 flex-wrap">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm text-gray-400">Fra</span>
          <DatepickerV2
            value={dateRange[0]}
            onChange={handleFromDateChange}
            className="w-40"
          />
          <span className="text-sm text-gray-400">Til</span>
          <DatepickerV2
            value={dateRange[1]}
            onChange={handleToDateChange}
            className="w-40"
          />
        </div>
        <Button
          color="primary"
          size="sm"
          onClick={handleGenerate}
          disabled={isLoading || !!dateYearMismatch}
          className="flex-shrink-0"
        >
          Hent
        </Button>
        {hasGenerated && hasData && (
          <Button
            color="primary-light"
            size="sm"
            onClick={handleExportExcel}
            className="text-white font-normal whitespace-nowrap flex-shrink-0"
            startIcon={<FaRegFileExcel className="text-brand-primary" />}
          >
            Eksporter excel
          </Button>
        )}
      </div>
      {renderContent()}
    </>
  );
}
